import { atom, type PrimitiveAtom } from "jotai";

import { authModalOpenAtom } from "@/hooks/useAuthModals";
import { atomWithLocalStorage } from "common/storage";

import {
  ONBOARDING_ANSWERS_KEY,
  ONBOARDING_COMPLETED_KEY,
  ONBOARDING_INTRO_STAGE_KEY,
  ONBOARDING_MINIMISED_KEY,
  ONBOARDING_PENDING_NEW_USER_KEY,
  ONBOARDING_STEP_KEY,
} from "../constants";
import { isLandingRouteExcluded } from "../utils/landingRoute";
import {
  EMPTY_ONBOARDING_ANSWERS,
  type OnboardingAnswers,
  type OnboardingBusinessClaim,
  type OnboardingStepName,
} from "../types";

/**
 * Where this browser is in the intro that opens on the first visit, before any
 * question is asked (`16204:290230`):
 *
 * - `welcome` — "Welcome to Sociability", the terms, "Agree and continue".
 * - `choice` — "Let's get started!": "I'll figure it out on my own" or "Show
 *   me how it works" (the six-page tour, which is in memory only - @see
 *   `onboardingV2IntroTourPageAtom`).
 * - `done` — either way out of the choice. The questions then carry on in the
 *   docked sheet as before.
 *
 * A fact about the **browser**, like a cookie banner: it survives log out, and
 * only a full reset puts it back.
 */
export type OnboardingIntroStage = "welcome" | "choice" | "done";

export const onboardingV2IntroStageAtom =
  atomWithLocalStorage<OnboardingIntroStage>(
    ONBOARDING_INTRO_STAGE_KEY,
    "welcome",
  );

/**
 * The tour page on screen, or `null` when the tour is not running. In memory:
 * a reload mid-tour comes back to "Let's get started!", which is one click
 * from where they were and lets them opt out instead.
 */
export const onboardingV2IntroTourPageAtom = atom<number | null>(
  null,
  // Without strictNullChecks, `null` matches jotai's read-only overload and
  // the setter types as `never` - the same cast as `useGeoLocation`'s.
) as PrimitiveAtom<number | null>;

/**
 * Whether the intro is owed. Never over the auth modal, for the same reason
 * the questions are not - @see `onboardingV2PresentationAtom`.
 */
/**
 * The "how it works" tour replayed on its own — the menu's "How the app
 * works". In memory, and separate from the stage: a replay neither re-asks the
 * terms nor puts the questions back, and ending it just closes the intro.
 */
export const onboardingV2IntroReplayAtom = atom(false);

export const onboardingV2IntroOpenAtom = atom(
  (get) =>
    !get(authModalOpenAtom) &&
    (get(onboardingV2IntroStageAtom) !== "done" ||
      get(onboardingV2IntroReplayAtom)),
);

/**
 * The claim form as typed so far, before it is submitted.
 *
 * The docked sheet follows the visitor from page to page, remounting the step
 * each time, so the form cannot keep its fields in component state without
 * losing them on every navigation. In memory rather than persisted like the
 * answers: a half-typed email and phone number have no business sitting in
 * localStorage, and native keeps its draft in the screen for the same reason.
 */
export const onboardingV2BusinessClaimDraftAtom = atom<OnboardingBusinessClaim | null>(
  null,
  // Same cast as `onboardingV2IntroTourPageAtom`: without strictNullChecks,
  // `null` matches jotai's read-only overload.
) as PrimitiveAtom<OnboardingBusinessClaim | null>;

/**
 * The flow is done with this user.
 *
 * Set when they pick a city — the only way native ever completes — and also
 * when they dismiss the full modal with the header's ✕, which the desktop frame
 * draws and native has no equivalent of. The docked sheet has no ✕, only a
 * minimise, so on it the city is the only way out. Dismissing counts as completing so the
 * flow asks once and then leaves people alone; the price is that a dismisser's
 * `answers.city` stays empty, exactly as a "Skip" on the carousel this replaces
 * left everything empty.
 *
 * Persisted on its **own** key. It never reads the legacy carousel's
 * `onboardingCompletedAtom` (`@/store/atoms`), so this flow runs once for
 * everyone, including everyone who already dismissed the old slider.
 */
export const onboardingV2CompletedAtom = atomWithLocalStorage<boolean>(
  ONBOARDING_COMPLETED_KEY,
  false,
);

/**
 * Everything the user has answered so far. Persisted, so quitting midway and
 * coming back re-opens with every earlier choice still selected — the steps
 * render straight off this, so that pre-filling needs no extra work. Never seed
 * local state from it.
 */
export const onboardingV2AnswersAtom = atomWithLocalStorage<OnboardingAnswers>(
  ONBOARDING_ANSWERS_KEY,
  EMPTY_ONBOARDING_ANSWERS,
);

/**
 * The step on screen. Persisted, unlike native — @see `ONBOARDING_STEP_KEY`.
 *
 * Can hold a step that is not on the current path (an old value from before a
 * step was renamed, or a branch that has since changed), so read it through
 * `useOnboardingFlow`, which falls back to the path's first step.
 */
export const onboardingV2StepAtom = atomWithLocalStorage<OnboardingStepName>(
  ONBOARDING_STEP_KEY,
  "USER_TYPE",
);

/**
 * Whether the visitor collapsed the docked sheet to its pill. `null` until they
 * have opened or collapsed it themselves, so the landing route can choose the
 * starting state without overriding a choice they made on an earlier visit.
 */
export const onboardingV2MinimisedPreferenceAtom = atomWithLocalStorage<
  boolean | null
>(ONBOARDING_MINIMISED_KEY, null);

/** The docked sheet is collapsed to its pill. @see the preference above. */
export const onboardingV2MinimisedAtom = atom(
  (get) => get(onboardingV2MinimisedPreferenceAtom) ?? isLandingRouteExcluded(),
);

/**
 * A sign-in or sign-up just completed in this tab, so the flow shows as the
 * full modal rather than the docked sheet — "the one that runs after the login
 * or register screen".
 *
 * In memory: the modal is the answer to having just signed in, not a mode to
 * restore. Reload mid-flow and the same step comes back docked.
 */
export const onboardingV2ModalRequestedAtom = atom(false);

/**
 * The visitor has done something inside the flow this page load — continued,
 * opened or collapsed the sheet.
 *
 * What lets the docked sheet move focus without stealing it. A modal takes
 * focus the moment it opens; a sheet that turns up in the corner of a page the
 * visitor is reading must not, but once they are working through it, focus
 * should follow them from step to step exactly as in the modal.
 */
export const onboardingV2EngagedAtom = atom(false);

/**
 * Set by the "How the app works" style replay entry points to force the flow
 * open again after it has been completed. In memory only — a replay is a thing
 * someone asked for now, not a state to restore next week.
 */
export const onboardingV2ReplayAtom = atom(false);

/**
 * A brand-new account signed up here and has not been through the flow yet.
 *
 * Separate from the completion flag because they answer different questions.
 * Completion is a fact about the *device* - "the flow has run in this browser"
 * - and is what stops it nagging a returning visitor. This is a debt owed by an
 * *account*, and it outranks completion: the second person to sign up on a
 * shared browser gets the flow even though the first already completed it.
 *
 * Settled by finishing the flow or dismissing it, and cleared on logout with
 * the rest of the session - @see `useClearOnboardingForSession`.
 */
export const onboardingV2PendingNewUserAtom = atomWithLocalStorage<boolean>(
  ONBOARDING_PENDING_NEW_USER_KEY,
  false,
);

/**
 * How the flow is on screen, if at all.
 *
 * - `modal` — the centred sheet over a dimmed page (`15984:127545`). Only right
 *   after a sign-in or sign-up, or when something asked for a replay.
 * - `docked` — the less intrusive version (`16150:269296`): the same steps in a
 *   sheet in the bottom-right corner, or collapsed to a progress pill, on
 *   every page until the visitor is done with it.
 * - `hidden` — nothing owed, or the auth modal is up.
 *
 * Derived rather than driven by an effect, so it is settled on the very first
 * render.
 */
export type OnboardingPresentation = "modal" | "docked" | "hidden";

export const onboardingV2PresentationAtom = atom<OnboardingPresentation>(
  (get) => {
    /*
     * Never over the auth flow. Sign-in and sign-up are modals of their own,
     * and a question stacked on top of someone entering a password is both
     * unreadable and a good way to lose them the sign-up. Native draws the
     * same line with its `UNINTERRUPTIBLE_ROOT_ROUTES` / auth-sheet check.
     * A requested modal waits here until the auth modal closes.
     */
    if (get(authModalOpenAtom)) return "hidden";

    if (get(onboardingV2ReplayAtom)) return "modal";

    // The questions wait for the intro. Whichever way the visitor leaves it,
    // they arrive in the docked sheet with anything answered before still
    // filled in.
    if (get(onboardingV2IntroStageAtom) !== "done") return "hidden";

    const isOwed =
      // A newly signed-up account is owed the flow even on a browser where
      // someone else already finished it.
      get(onboardingV2PendingNewUserAtom) || !get(onboardingV2CompletedAtom);
    if (!isOwed) return "hidden";

    return get(onboardingV2ModalRequestedAtom) ? "modal" : "docked";
  },
);

/**
 * Whether the full modal is on screen.
 */
export const onboardingV2OpenAtom = atom(
  (get) => get(onboardingV2PresentationAtom) === "modal",
);

/**
 * Whether onboarding is taking up the screen: the modal, or the docked sheet
 * while it is expanded. Its collapsed pill does not count.
 *
 * What other first-run overlays wait on — the home tour's coach marks would
 * otherwise arrive on top of, or next to, a question the visitor is answering.
 */
export const onboardingV2ExpandedAtom = atom((get) => {
  if (get(onboardingV2IntroOpenAtom)) return true;
  const presentation = get(onboardingV2PresentationAtom);
  if (presentation === "modal") return true;
  return presentation === "docked" && !get(onboardingV2MinimisedAtom);
});
